import { useState, useCallback, useMemo } from 'react';
import { useGetOrganizationQuery } from '../../../hooks/queries/useGetOrganizationQuery';
import { useGetSSOStatusQuery } from '../../../hooks/queries/useGetSSOStatusQuery';
import { useEnableSSSOMutation } from '../../../hooks/mutations/useEnableSSSOMutation';
import { useDisableSSSOMutation } from '../../../hooks/mutations/useDisableSSSOMutation';
import { EnableSSORequest } from '../../../api/sso';
import { SSOFormValidator } from '../services/SSOFormValidator';
import { SSOUIStateManager } from '../services/SSOUIStateManager';

/**
 * Custom hook that encapsulates all SSO settings business logic.
 * Separates state management and business rules from the component.
 */
export function useSSOSettings() {
  const [showEnableForm, setShowEnableForm] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const { data: organization, isLoading: isLoadingOrg } = useGetOrganizationQuery();
  const {
    data: ssoStatus,
    isLoading: isLoadingSSO,
    error: ssoQueryError,
  } = useGetSSOStatusQuery(organization?.id || 0);

  const enableMutation = useEnableSSSOMutation();
  const disableMutation = useDisableSSSOMutation();

  // Initialize services
  const validator = useMemo(() => new SSOFormValidator(), []);
  const uiStateManager = useMemo(() => new SSOUIStateManager(), []);

  // Derived state
  const isLoading = isLoadingOrg || isLoadingSSO;
  const error = ssoQueryError || enableMutation.error || disableMutation.error;
  const isSuccess = enableMutation.isSuccess || disableMutation.isSuccess;
  const hasDisabledConnection = useMemo(
    () => ssoStatus?.connections
      ? uiStateManager.hasDisabledConnection(ssoStatus.connections)
      : false,
    [ssoStatus, uiStateManager]
  );

  /**
   * Handles SSO enablement form submission.
   * Validates input and delegates to the appropriate mutation.
   */
  const handleEnableSSO = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!organization?.id) return;

      const form = e.currentTarget;
      const formData = new FormData(form);
      const formValues = {
        entraIdTenantId: formData.get('entraIdTenantId') as string,
        entraIdClientId: formData.get('entraIdClientId') as string,
        entraIdClientSecret: formData.get('entraIdClientSecret') as string,
        entraIdDomain: formData.get('entraIdDomain') as string,
      };

      const validation = validator.validate(formValues);

      if (!validation.success) {
        setFormErrors(validation.errors || {});
        return;
      }

      setFormErrors({});

      const requestData: EnableSSORequest = {
        connectionType: 'ENTRA_ID',
        entraIdTenantId: validation.data?.entraIdTenantId || undefined,
        entraIdClientId: validation.data?.entraIdClientId || undefined,
        entraIdClientSecret: validation.data?.entraIdClientSecret || undefined,
        entraIdDomain: validation.data?.entraIdDomain || undefined,
      };

      enableMutation.mutate(
        {
          organizationId: organization.id,
          data: requestData,
        },
        {
          onSuccess: () => {
            setShowEnableForm(false);
            form.reset();
          },
        }
      );
    },
    [organization, validator, enableMutation]
  );

  /**
   * Handles SSO disablement with user confirmation.
   */
  const handleDisableSSO = useCallback(
    (connectionId: number, confirmMessage: string) => {
      if (!organization?.id) return;

      if (!confirm(confirmMessage)) {
        return;
      }

      disableMutation.mutate({
        organizationId: organization.id,
        connectionId,
      });
    },
    [organization, disableMutation]
  );

  /**
   * Opens the enable form.
   */
  const openEnableForm = useCallback(() => {
    setShowEnableForm(true);
  }, []);

  /**
   * Closes the enable form.
   */
  const closeEnableForm = useCallback(() => {
    setShowEnableForm(false);
    setFormErrors({});
  }, []);

  /**
   * Checks if a field should be required.
   */
  const isFieldRequired = useCallback(
    (fieldName: string) => {
      return validator.isFieldRequired(fieldName, hasDisabledConnection);
    },
    [validator, hasDisabledConnection]
  );

  return {
    // Data
    organization,
    ssoStatus,

    // Loading states
    isLoading,

    // Error states
    error,
    isSuccess,

    // Form state
    showEnableForm,
    formErrors,

    // Mutation states
    isEnabling: enableMutation.isPending,

    // Actions
    handleEnableSSO,
    handleDisableSSO,
    openEnableForm,
    closeEnableForm,

    // Helpers
    isFieldRequired,
    uiStateManager,
  };
}
